import { useState } from 'react';
import * as Crypto from 'expo-crypto';
import * as ImagePicker from 'expo-image-picker';
import { supabase } from '@/lib/supabase';
import { trackEvent } from '@/lib/analytics';

export function useSendMessage(
  conversationId: string,
  senderId: string
): {
  sendText: (content: string) => Promise<void>;
  sendPhoto: () => Promise<void>;
  isSending: boolean;
} {
  const [isSending, setIsSending] = useState(false);

  async function sendText(content: string): Promise<void> {
    if (!content.trim()) return;
    setIsSending(true);
    try {
      const { error } = await supabase.from('messages').insert({
        conversation_id: conversationId,
        sender_id: senderId,
        content: content.trim(),
      });
      if (error) throw error;
      trackEvent('chat_message_sent', { conversationId, type: 'text', length: content.trim().length });
    } finally {
      setIsSending(false);
    }
  }

  async function sendPhoto(): Promise<void> {
    const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permission.granted) return;

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      quality: 0.7,
    });
    if (result.canceled) return;

    setIsSending(true);
    try {
      const uri = result.assets[0].uri;
      const ext = uri.split('.').pop() ?? 'jpg';
      const fileName = `${conversationId}/${Crypto.randomUUID()}.${ext}`;

      const response = await fetch(uri);
      const blob = await response.blob();

      const { error: uploadError } = await supabase.storage
        .from('chat-photos')
        .upload(fileName, blob, { contentType: `image/${ext}` });
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('chat-photos')
        .getPublicUrl(fileName);

      const { error: insertError } = await supabase.from('messages').insert({
        conversation_id: conversationId,
        sender_id: senderId,
        photo_url: publicUrl,
      });
      if (insertError) throw insertError;
      trackEvent('chat_message_sent', { conversationId, type: 'photo' });
    } finally {
      setIsSending(false);
    }
  }

  return { sendText, sendPhoto, isSending };
}
